<script lang="ts">
	import Uppy, { type UploadResult, type Meta, type UppyFile } from "@uppy/core"
	import Dashboard from "@uppy/svelte/dashboard"

	import "@uppy/core/css/style.min.css"
	import "@uppy/dashboard/css/style.min.css"

	import Tus from "@uppy/tus"
	import { onDestroy } from "svelte"
	import { UploadError } from "../model/errors"

	type UploadPropBase = {
		endpoint: string
		allowedMimeTypes: Array<string>
		maxFileLimit?: number
		/**
		 * Mints the signed upload grant (LuxedoRPC.api.upload.tus_upload_token) right before the
		 * bytes move. It is attached as the `upload_token` metadata field, and tusd's hook derives
		 * the uploading user from it; nothing else in the metadata is trusted for identity.
		 */
		getUploadToken: () => Promise<string>
		/**
		 * Bindable boolean to show uploading state
		 */
		isUploading?: boolean
		/**
		 * Called when one upload completes, with the file as Uppy tracks it (its tus upload
		 * URL is on `file.tus.uploadUrl`; see `getTusUploadId`).
		 */
		onUploadComplete?: (file?: UppyFile<Meta, Record<string, never>>) => void
		/**
		 * Called when ALL uploads have completed (or errored)
		 * @param res
		 */
		onAllComplete?: (res: UploadResult<Meta, Record<string, never>>) => void
		/**
		 * Called when the upload begins
		 */
		onStart?: (args?: [files: UppyFile<Meta, Record<string, never>>[]]) => void
		/**
		 * Called when an upload error occurs
		 * @param err
		 */
		onError?: (err: UploadError) => void
		/**
		 * Bindable function that can be called to trigger the upload.
		 */
		triggerUpload?: () => void
		/**
		 * Bindable function that can be called to clear all current uploads and empty files.
		 */
		clearUploads?: () => void
	}

	type BaseMeta = {
		[index: string]: any
		folder_id: number
		entry_id?: number
	}

	interface SceneMeta extends BaseMeta {
		title: string
		device_id: number
		deep_fry: "off" | "low" | "med" | "high"
	}

	interface MediaMeta extends BaseMeta {}

	interface SceneProps extends UploadPropBase {
		uploadType: "scene"
		meta: SceneMeta
	}

	interface MediaProps extends UploadPropBase {
		uploadType: "media"
		meta: MediaMeta
	}

	type Props = MediaProps | SceneProps

	let {
		endpoint,
		uploadType,
		getUploadToken,
		meta,
		maxFileLimit = 15,
		allowedMimeTypes,
		onUploadComplete,
		onAllComplete,
		onStart,
		onError,
		isUploading = $bindable(),
		triggerUpload = $bindable(),
		clearUploads = $bindable(),
	}: Props = $props()

	let nonce = Date.now()

	const DEFAULT_META_FIELDS = ["type", "name"] as const

	/**
	 * Pick me up pick me up pick me up
	 */
	function giveMeUppies() {
		const restrictions = {
			allowedFileTypes: allowedMimeTypes,
		}

		if (maxFileLimit) {
			restrictions["maxNumberOfFiles"] = maxFileLimit
		}

		const uppy = new Uppy({
			autoProceed: false,
			restrictions,
		}).use(Tus, {
			endpoint,
			allowedMetaFields: [...DEFAULT_META_FIELDS],
			removeFingerprintOnSuccess: true,
			chunkSize: 128 * 1024 * 1024,
			limit: 4,
		})

		// Identity is a signed grant minted per upload batch, not a user id the browser claims.
		// Minted here, right before the bytes move, so a long-open dialog never uploads on a stale one.
		uppy.addPreProcessor(async () => {
			try {
				uploadToken = await getUploadToken()
			} catch (e) {
				const reason = e instanceof Error ? e.message : String(e)
				const err = new UploadError(null, `Could not start the upload: no upload grant (${reason})`)
				onError?.(err)
				throw err
			}
			applyMeta()
		})

		uppy.on("upload-success", (file) => {
			syncUploadingState()
			onUploadComplete?.(file)
		})

		if (onAllComplete)
			uppy.on("complete", (e) => {
				syncUploadingState()
				onAllComplete?.(e)
			})
		uppy.on("upload-start", (...args) => {
			isUploading = true
			onStart?.(args)
		})
		uppy.on("upload-error", (file, error) => {
			syncUploadingState()
			const [code, message] = getTusStatusAndMessage(error)
			onError?.(new UploadError(code, message))
		})
		uppy.on("cancel-all", () => (isUploading = false))
		uppy.on("file-removed", syncUploadingState)

		return uppy
	}

	const uppy = giveMeUppies()

	/** The grant for the batch in flight; set by the pre-processor, sent as `upload_token`. */
	let uploadToken: string | null = null

	/**
	 * Parses the meta object, removing any properties with undefined values
	 */
	function parseMeta(meta: SceneMeta | MediaMeta) {
		const obj: Partial<SceneMeta | MediaMeta> = {
			upload_type: uploadType,
		}
		if (uploadToken) obj.upload_token = uploadToken
		for (const [k, v] of Object.entries(meta ?? {})) {
			if (v !== undefined && v !== null) obj[k] = v
		}

		return obj
	}

	/**
	 * Parses the http error code from the tusd error.
	 * This is a VERY messy way to do this, but I haven't been able to find another way to access the status code from webhook responses to the tusd container.
	 * @param error
	 */
	function getTusStatusAndMessage(error: any): [number | null, string | null] {
		// Structured status from XHR snapshots
		const sentryStatus =
			error?.originalResponse?._xhr?.__sentry_xhr_v3__?.status_code ??
			error?.originalRequest?._xhr?.__sentry_xhr_v3__?.status_code
		const structuredStatus = typeof sentryStatus === "number" ? sentryStatus : null

		// Structured body if present
		const structuredBody =
			error?.originalResponse?.getBody?.() ??
			error?.originalResponse?._xhr?.__sentry_xhr_v3__?.body ??
			null

		// Fallback: parse from tus error message text
		const msgText = error?.message || ""
		const match = msgText.match(/response code:\s*(\d{3})/i)
		const parsedStatus = match ? Number(match[1]) : null
		const parsedBodyMatch = msgText.match(/response text:\s*([^,]+)(?:,|$)/i)
		const parsedBody = parsedBodyMatch ? parsedBodyMatch[1].trim() : null

		const status = structuredStatus ?? parsedStatus
		const message = structuredBody ?? parsedBody ?? null
		return [status, message]
	}

	/**
	 * Updates the isUploading state depending on uppy's current upload state
	 */
	function syncUploadingState() {
		const { currentUploads } = uppy.getState()
		isUploading = Object.keys(currentUploads).length > 0
	}

	/**
	 * Bindable function that can be called to trigger the upload.
	 * This was added for backwards compatibility - the direct upload overlay hides the upload button and triggers via this method
	 */
	triggerUpload = () => {
		if (uppy.getFiles().length === 0) throw new Error("No files selected for upload")
		// A grant that cannot be minted rejects here after the pre-processor has already reported it
		// (onError + Uppy's own "Upload failed" status); an unhandled rejection would add nothing.
		void uppy.upload().catch((err) => console.error("Upload did not start", err))
	}

	/**
	 * Bindable function that can be called to clear all current files.
	 */
	clearUploads = () => {
		uppy.cancelAll()
	}

	/** Push the current meta (plus the grant, once minted) into uppy and the tus plugin's allow-list. */
	function applyMeta() {
		const newMeta = parseMeta(meta)
		const tus = uppy.getPlugin("Tus")

		tus?.setOptions({
			allowedMetaFields: [...Object.keys(newMeta), ...DEFAULT_META_FIELDS],
		})

		uppy.setMeta({})
		uppy.setMeta(newMeta)
	}

	// Update the metafields when the meta values change
	$effect(() => {
		applyMeta()
	})

	// clear all the current uploads if the component gets destroyed before they complete
	onDestroy(() => {
		clearUploads()
	})
</script>

<div id={`file-uploader-${nonce}`} class="file-uploader">
	<Dashboard {uppy} props={{ width: "100%", height: "100%", inline: true }} />
</div>

<style>
	.file-uploader {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
	}

	.file-uploader :global(.uppy-Container),
	.file-uploader :global(.uppy-Root),
	.file-uploader :global(.uppy-Dashboard) {
		height: 100%;
		width: 100%;
	}

	.file-uploader :global(.uppy-Dashboard-dropFilesHereHint) {
		border-radius: 1rem;
	}

	.file-uploader :global(.uppy-Dashboard-inner) {
		background-color: var(--color-bg);
		border: none;
	}

	.file-uploader :global(.uppy-Dashboard-Item-name),
	.file-uploader :global(.uppy-DashboardContent-title),
	.file-uploader :global(.uppy-Dashboard-AddFiles-title) {
		color: var(--color-text-light);
	}

	.file-uploader :global(.uppy-Dashboard-AddFiles) {
		border: none;
	}

	.file-uploader :global(.uppy-Dashboard-AddFiles-info) {
		display: none;
	}

	.file-uploader :global(.uppy-Container button) {
		transform: none;
	}

	.file-uploader :global(.uppy-DashboardContent-bar) {
		padding: 0.25rem 1.2rem 0 1.2rem;
		background-color: var(--color-bg);
		border: none;
	}

	.file-uploader :global(.uppy-DashboardContent-bar button) {
		background-color: transparent;
		/* border: 2px solid var(--color-main); */
		border: none;
		box-shadow: none;
		color: var(--color-text-light);
		border-radius: 99px;
		padding: 0.5rem 1rem;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.file-uploader :global(.uppy-DashboardContent-bar button:hover),
	.file-uploader :global(.uppy-DashboardContent-bar button:focus-visible) {
		background-color: transparent;
		color: var(--color-main);
	}

	.file-uploader :global(.uppy-DashboardContent-addMoreCaption) {
		margin-left: 0.25rem;
	}

	.file-uploader :global(.uppy-StatusBar) {
		background-color: transparent;
		border: none;
	}

	.file-uploader :global(.uppy-StatusBar-actions) {
		background-color: transparent;
		border: none;
		justify-content: flex-end;
	}

	.file-uploader :global(.uppy-StatusBar:before) {
		content: unset;
	}

	.file-uploader :global(.uppy-StatusBar-actions button) {
		border-radius: 99px;
	}

	.file-uploader :global(.uppy-StatusBar-actionBtn--upload) {
		background-color: var(--color-main);
	}

	.file-uploader :global(.uppy-DashboardContent-addMore) {
		width: unset;
		height: unset;
	}

	.file-uploader :global(.uppy-Dashboard-browse) {
		color: var(--color-main);
		border-color: var(--color-main);
	}

	.file-uploader :global(.uppy-c-icon:not(.uppy-StatusBar-statusIndicator)) {
		margin-inline-end: 0;
	}

	.file-uploader :global(.uppy-Dashboard-AddFilesPanel) {
		background: var(--color-bg-darker);
	}

	.file-uploader :global(.uppy-StatusBar-spinner) {
		fill: var(--color-main);
	}

	.file-uploader :global(.uppy-StatusBar-progress) {
		background-color: var(--color-main);
	}

	.file-uploader :global(.uppy-StatusBar-status) {
		color: var(--color-text-light);
	}

	.file-uploader :global(.uppy-StatusBar-statusSecondary) {
		color: var(--color-text);
	}
</style>
